/**
 * AuthContext — identity + JWT management for LAUNCHER-2.0.
 *
 * Auth flow:
 *  1. Existing JWT in localStorage (survives page refresh)
 *  2. Entra ID SSO via Container Apps Easy Auth — called explicitly via ssoLogin()
 *
 * SSO is the only login method. Email + password authentication has been removed.
 *
 * Exposes:
 *   user          — null | { ntid, oid, full_name, email, dept, role_id }
 *   ssoLogin()    — Entra SSO exchange (call on mount if Easy Auth is active)
 *   logout()      — clears user + token
 *   isLoading     — true while an auth request is in flight
 *   ssoChecked    — true once the /.auth/me check has completed
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { getToken, setToken, clearToken, isAuthenticated } from '../services/tokenStore';

const AuthContext = createContext(null);

function _jwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const p = _jwtPayload(token);
  if (!p?.exp) return true;
  return p.exp < Math.floor(Date.now() / 1000);
}

function decodeUser(token) {
  const payload = _jwtPayload(token);
  if (!payload?.sub) return null;
  if (isTokenExpired(token)) return null;
  return {
    ntid: payload.ntid || payload.sub,
    oid: payload.oid || '',
    email: payload.email || '',
    full_name: payload.full_name || payload.sub,
    dept: payload.dept || '',
    role_id: payload.role_id || 1,
  };
}

export { isTokenExpired };

export function AuthProvider({ children }) {
  // Clear any expired token immediately so the rest of the app sees a clean state.
  // This runs synchronously before state initialization so isAuthenticated() / getToken()
  // reflect the true session state from the first render.
  const _stored = getToken();
  if (_stored && isTokenExpired(_stored)) {
    clearToken();
  }

  const [user, setUser] = useState(() => {
    if (!isAuthenticated()) return null;
    return decodeUser(getToken());
  });
  const [isLoading, setIsLoading] = useState(false);
  // ssoChecked: true once /.auth/me check has completed (or a valid JWT already exists)
  const [ssoChecked, setSsoChecked] = useState(() => isAuthenticated());

  // ── SSO: read X-MS-CLIENT-PRINCIPAL via backend (Container Apps Easy Auth) ─
  // Container Apps Easy Auth injects X-MS-CLIENT-PRINCIPAL into every proxied
  // request from an authenticated user. /auth/sso-callback reads that header
  // and issues an internal JWT, avoiding /.auth/me (which nginx serves as
  // index.html in AllowAnonymous mode, breaking JSON.parse).
  const ssoLogin = useCallback(async () => {
    if (ssoChecked && user) return true; // already logged in
    setIsLoading(true);
    try {
      const res = await fetch('/auth/sso-callback');
      if (!res.ok) {
        // 401 = not authenticated (normal on first visit before SSO sign-in)
        if (res.status !== 401) {
          const err = await res.json().catch(() => ({}));
          console.error('[SSO] sso-callback error:', err.detail || res.status);
        }
        setSsoChecked(true);
        return false;
      }
      const data = await res.json();
      const token = data.access_token || data.token;
      if (token) {
        setToken(token);
        setUser(decodeUser(token));
      }
      setSsoChecked(true);
      return true;
    } catch (err) {
      console.warn('[SSO] sso-callback unavailable (dev mode):', err.message);
      setSsoChecked(true);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [ssoChecked, user]);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setSsoChecked(false);
    // In production Easy Auth owns the session; clear it via the sidecar logout endpoint.
    if (import.meta.env.VITE_USE_REAL_BACKEND) {
      window.location.href = '/.auth/logout?post_logout_redirect_uri=' + encodeURIComponent(window.location.origin + '/');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, ssoLogin, logout, isLoading, ssoChecked }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
